import { SVGProps } from "react";

/**
 * Hand-coded SVGs for icons used above the mobile fold. These avoid
 * pulling the 513 KiB `vendor-icons` chunk into the LCP critical path.
 * Geometry matches lucide-react's 24x24 grid with stroke-width 2 so the
 * visual swap is invisible. Pass standard SVG props (className, etc.).
 */
type IconProps = SVGProps<SVGSVGElement>;

const base = {
  xmlns: "http://www.w3.org/2000/svg",
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

export const MenuIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);

export const XIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const PhoneIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

export const ChevronDownIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const MessageCircleIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

export const LogInIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);

export const LogOutIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

export const UserIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const SearchIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

export const WrenchIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

// --- Mobile class tiles (Caravan / Truck / Bus / CarFront / Container / Tent / Home / Shrub) ---

export const CaravanIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M2 17v-6a2 2 0 0 1 2-2h13a3 3 0 0 1 3 3v5H2z" />
    <path d="M20 17h2v-2a4 4 0 0 0-2-3" />
    <rect x="6" y="11" width="5" height="4" />
    <circle cx="8" cy="19" r="2" />
  </svg>
);

export const TruckIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M5 17h-2v-11a1 1 0 0 1 1-1h9v12" />
    <path d="M14 9h4l4 4v4h-3" />
    <circle cx="7" cy="18" r="2" />
    <circle cx="17" cy="18" r="2" />
  </svg>
);

export const BusIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M8 6v6" />
    <path d="M15 6v6" />
    <path d="M2 12h19.6" />
    <path d="M18 18h3a1 1 0 0 0 1-1v-7.31a4 4 0 0 0-.6-2.1L19.4 4.6A2 2 0 0 0 17.69 4H3a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2" />
    <circle cx="7" cy="18" r="2" />
    <circle cx="16" cy="18" r="2" />
  </svg>
);

export const CarFrontIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M21 8 17.5 4.5A2 2 0 0 0 16 4H8a2 2 0 0 0-1.5.5L3 8" />
    <path d="M3 8h18v8a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-1H7v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
    <path d="M7 13h2" />
    <path d="M15 13h2" />
  </svg>
);

export const ContainerIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M22 7.7a2 2 0 0 0-1-1.7l-8-4.5a2 2 0 0 0-2 0L3 6a2 2 0 0 0-1 1.7v8.6A2 2 0 0 0 3 18l8 4.5a2 2 0 0 0 2 0L21 18a2 2 0 0 0 1-1.7z" />
    <path d="M10 21.9V14L2.1 9.1" />
    <path d="M10 14 21.9 9.1" />
    <path d="M14 19.8V12.7" />
    <path d="M14 12.7 21.9 8" />
  </svg>
);

export const TentIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M3.5 21 14 3" />
    <path d="M20.5 21 10 3" />
    <path d="M15.5 21 12 15l-3.5 6" />
    <path d="M2 21h20" />
  </svg>
);

export const HomeIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

export const ShrubIcon = (p: IconProps) => (
  <svg {...base} {...p}>
    <path d="M12 22v-7l-2-2" />
    <path d="M17 8v.8A6 6 0 0 1 13.8 20v0H10v0A6.5 6.5 0 0 1 7 8h0a5 5 0 0 1 10 0Z" />
    <path d="m14 14-2 2" />
  </svg>
);